import { useState, useEffect } from "react";
import { useForm } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { useGo, useInvalidate } from "@refinedev/core";

import { Card, Form, Input, Switch, Select, Space, Button, Radio, Alert, Tag } from "antd";

const { TextArea } = Input;

const API_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:8085";

interface StopWord {
  id: string;
  word: string;
  category: string;
  action: string;
  is_active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface StopWordFormValues {
  word: string;
  category: string;
  action: string;
  is_active: boolean;
  description?: string;
}

interface StopWordCategory {
  id: string;
  label: string;
  color: string;
  action_type: string;
  description?: string;
}

export const StopWordEditForm = () => {
  const go = useGo();
  const invalidate = useInvalidate();

  // Получаем категории из API
  const [categories, setCategories] = useState<StopWordCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/v1/stop-word-categories`)
      .then(res => res.json())
      .then(data => {
        setCategories(data.items || []);
        setCategoriesLoading(false);
      })
      .catch(() => setCategoriesLoading(false));
  }, []);

  const { formProps, formLoading } = useForm<
    StopWord,
    HttpError,
    StopWordFormValues
  >({
    redirect: false,
    mutationMode: "pessimistic",
    onMutationSuccess: () => {
      invalidate({ resource: "stop-words", invalidates: ["resourceAll"] });
      go({
        to: "/stop-words",
        type: "replace",
        options: { keepQuery: true },
      });
    },
    successNotification: () => ({
      type: "success",
      message: "Успешно",
      description: "Стоп-слово обновлено",
    }),
    errorNotification: (error) => ({
      type: "error",
      message: "Не удалось сохранить",
      description:
        error?.message || "Проверьте обязательные поля и повторите попытку",
    }),
  });

  const handleFinish = async (values: StopWordFormValues) => {
    const payload = {
      ...values,
      word: values.word.trim(),
    };
    await formProps.onFinish?.(payload);
  };

  return (
    <Card title="Редактирование стоп-слова" loading={formLoading} bordered>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
      >
        <Form.Item
          label="Слово или фраза"
          name="word"
          rules={[
            { required: true, message: "Введите слово или фразу" },
            { min: 2, message: "Минимум 2 символа" },
          ]}
        >
          <Input
            placeholder="Например: суд, прокуратура, аллергия"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Категория"
          name="category"
          rules={[{ required: true, message: "Выберите категорию" }]}
          tooltip="Тип проблемы или темы, к которой относится стоп-слово"
        >
          <Select
            placeholder="Выберите категорию"
            size="large"
            loading={categoriesLoading}
            showSearch
            optionFilterProp="label"
            options={categories.map((cat) => ({
              value: cat.id,
              label: cat.label,
              category: cat,
            }))}
            optionRender={(option) => {
              const cat = option.data.category as StopWordCategory;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag color={cat.color} style={{ margin: 0 }}>{cat.label}</Tag>
                  {cat.description && (
                    <>
                      <span style={{ fontSize: 12, color: '#999' }}>—</span>
                      <span style={{ fontSize: 12, color: '#666' }}>
                        {cat.description}
                      </span>
                    </>
                  )}
                </div>
              );
            }}
          />
        </Form.Item>

        <Form.Item
          label="Действие при обнаружении"
          name="action"
          rules={[{ required: true, message: "Выберите действие" }]}
          tooltip="Блокировка — отзыв отправляется на ручную модерацию. Триггер — слово используется для маршрутизации на специальные ответы."
        >
          <Radio.Group>
            <Radio.Button value="block">
              Блокировка
            </Radio.Button>
            <Radio.Button value="trigger">
              Триггер
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.action !== cur.action}>
          {({ getFieldValue }) => {
            const action = getFieldValue("action");
            return action === "block" ? (
              <Alert
                message="Блокировка"
                description="При обнаружении этого слова отзыв будет помечен как требующий ручной модерации (has_stop_words = true)"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Alert
                message="Триггер"
                description="Слово будет использоваться для маршрутизации на специальные ответы ИИ. Категория слова будет добавлена в trigger_word_categories отзыва."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            );
          }}
        </Form.Item>

        <Form.Item
          label="Активно"
          name="is_active"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item label="Описание" name="description">
          <TextArea
            rows={3}
            placeholder="Опишите причину добавления стоп-слова"
          />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={formLoading}>
            Сохранить
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
